import { Score, ScoreDimension, ScoreDimensionValue, ScoreWithStatistics } from "src/api/client-api/post/scoreSchema";
import { ScoreDetailBuilder } from "src/context/ScoreDetail/ScoreDetailBuilder";

type Options = {
    scores: Score | undefined;
    prevScores?: Score | undefined;
    scoresAvg?: Score | undefined;
    scoresMax?: Score | undefined;
    scoresMin?: Score | undefined;
    scoresCount?: Score | undefined;
};
export const scoresTreeMerge = (options: Options): Score<ScoreWithStatistics> | null => {
    const { scores, scoresAvg, scoresMax, scoresMin, scoresCount, prevScores } = options;

    if (scores === undefined) return null;

    const { score: scoreValue, ...metaScoreAttr } = scores;
    const groupedScore: ScoreWithStatistics = {
        value: scoreValue,
        prev: prevScores?.score ?? null,
        avg: scoresAvg?.score ?? null,
        max: scoresMax?.score ?? null,
        min: scoresMin?.score ?? null,
        count: scoresCount?.score ?? null,
    };

    const mergedDimensions = mergeDimensions({
        scores: scores.dimensions,
        prevScores: prevScores?.dimensions ?? [],
        scoresAvg: scoresAvg?.dimensions ?? [],
        scoresMax: scoresMax?.dimensions ?? [],
        scoresMin: scoresMin?.dimensions ?? [],
        scoresCount: scoresCount?.dimensions ?? [],
    });

    const mergedScores = {
        ...metaScoreAttr,

        score: groupedScore,
        dimensions: mergedDimensions,
    };

    return mergedScores;
};

type MergeDimensionOptions = {
    scores: ScoreDimension[];
    prevScores: ScoreDimension[];
    scoresAvg: ScoreDimension[];
    scoresMin: ScoreDimension[];
    scoresMax: ScoreDimension[];
    scoresCount: ScoreDimension[];
};

type MergedDimensions = ScoreDimension<ScoreWithStatistics>[];
const mergeDimensions = (options: MergeDimensionOptions): MergedDimensions => {
    const { scores, scoresAvg, scoresMax, scoresMin, prevScores, scoresCount } = options;
    if (scores.length === 0) return [];

    const mergedDimensions = scores.map((scoreDimension) => {
        const dimensionToSeek = scoreDimension.label;

        const avgDimensions = ScoreDetailBuilder.drilldownDimension(scoresAvg, dimensionToSeek);
        const minDimensions = ScoreDetailBuilder.drilldownDimension(scoresMin, dimensionToSeek);
        const maxDimensions = ScoreDetailBuilder.drilldownDimension(scoresMax, dimensionToSeek);
        const countDimensions = ScoreDetailBuilder.drilldownDimension(scoresCount, dimensionToSeek);
        const prevDimensions = ScoreDetailBuilder.drilldownDimension(prevScores, dimensionToSeek);

        const acc: Record<string, ScoreDimensionValue<ScoreWithStatistics>> = {};
        const mergedDimensionValues = Object.entries(scoreDimension.values).reduce((dimAcc, [key, value]) => {
            const avgValue = avgDimensions?.[key];
            const minValue = minDimensions?.[key];
            const maxValue = maxDimensions?.[key];
            const countValue = countDimensions?.[key];
            const prevValue = prevDimensions?.[key];

            const innerDimensions = mergeDimensions({
                scores: value.dimensions ?? [],
                prevScores: prevValue?.dimensions ?? [],
                scoresAvg: avgValue?.dimensions ?? [],
                scoresMax: maxValue?.dimensions ?? [],
                scoresMin: minValue?.dimensions ?? [],
                scoresCount: countValue?.dimensions ?? [],
            });
            const mergedValues: ScoreDimensionValue<ScoreWithStatistics> = {
                position: null,
                label: value.label,
                score: {
                    value: value.score,
                    prev: prevValue?.score ?? null,
                    avg: avgValue?.score ?? null,
                    min: minValue?.score ?? null,
                    max: maxValue?.score ?? null,
                    count: countValue?.score ?? null,
                },
                dimensions: innerDimensions,
            };
            dimAcc[key] = mergedValues;
            return dimAcc;
        }, acc);

        const mergedDimension: ScoreDimension<ScoreWithStatistics> = {
            label: scoreDimension.label,
            values: mergedDimensionValues,
        };
        return mergedDimension;
    });

    return mergedDimensions;
};
